import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, BehaviorSubject, of, throwError } from 'rxjs';
import { map, catchError, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { GenericConstants } from 'src/app/core/enums/generic-constants.enum';
import { LanguageService } from './language.service';
import {
  ChatMessage,
  ChatConversation,
  HelpCollection,
  HelpArticle
} from 'src/app/core/models/chatbot/chat-message.model';
import {
  ChatResponse,
  SendMessageRequest,
  ConversationListResponse,
  HelpArticleResponse
} from 'src/app/core/models/chatbot/chat-response.model';
import {
  ChatRequestDto,
  ChatResponseDto,
  ConversationDto,
  MessageDto,
  PaginatedConversationsResponse,
  PaginatedMessagesResponse,
  PaginatedConversationsResult,
  PaginatedMessagesResult
} from 'src/app/core/models/chatbot/chatbot-api.model'; @Injectable({
  providedIn: 'root'
})
export class ChatbotService {
  private baseUrl = environment.baseUrl;
  private helpCenterUrl = `${environment.baseUrl}admin/help-center`;  // Help Center Admin API
  private readonly STORAGE_KEY = 'chatbot_conversations';
  private readonly CURRENT_CONVERSATION_KEY = 'chatbot_current_conversation';

  private conversationsSubject = new BehaviorSubject<ChatConversation[]>([]);
  public conversations$ = this.conversationsSubject.asObservable();

  private currentConversationSubject = new BehaviorSubject<ChatConversation | null>(null);
  public currentConversation$ = this.currentConversationSubject.asObservable();

  constructor(
    private http: HttpClient,
    private languageService: LanguageService
  ) {
    this.loadConversationsFromStorage();
  }

  // ============================================
  // Private Utility Methods
  // ============================================

  private getUserId(): string | null {
    return localStorage.getItem(GenericConstants.STORAGE_USER_ID);
  }

  private getLocale(): string {
    const lang = this.languageService.getCurrentLang();
    const localeMap: { [key: string]: string } = {
      'en': 'en-US',
      'es': 'es-ES',
      'ar': 'ar-MA',
      'fr': 'fr-FR'
    };
    return localeMap[lang] || 'en-US';
  }

  private mapConversationDtoToModel(dto: ConversationDto): ChatConversation {
    return {
      id: dto.id,
      title: dto.label || 'Untitled Conversation',
      lastMessage: dto.lastMessage || '',
      timestamp: new Date(dto.updatedAt),
      messages: [],
      unreadCount: 0
    };
  }

  private mapMessageDtoToModel(dto: MessageDto): ChatMessage {
    return {
      id: dto.id,
      text: dto.content,
      isUser: dto.role.toLowerCase() === 'user',
      timestamp: new Date(dto.createdAt),
      type: 'text'
    };
  }

  // ============================================
  // Public API Methods
  // ============================================

  // Send message to chatbot
  sendMessage(message: string, conversationId?: string): Observable<ChatResponse> {
    const headers = this.getHeaders();

    const requestBody: ChatRequestDto = {
      tenantId: 'naaora',
      sessionId: '',
      userId: this.getUserId(),
      message: message,
      conversationId: conversationId,
      context: null,
      attachedDocumentIds: [],
      locale: this.getLocale()
    };

    return this.http.post<ChatResponseDto>(
      `${environment.chatbotApiUrl}/v1/chat`,
      requestBody,
      { headers }
    ).pipe(
      map(response => ({
        message: response.answer,
        type: 'text' as const,
        conversationId: response.conversationId,
        extraData: response.extraData
      })),
      catchError(error => {
        console.error('Error sending chat message:', error);

        // If it's a 429 (Too Many Requests), let it pass through to the component
        if (error.status === 429) {
          return throwError(() => error);
        }

        // For other errors, return generic error message
        return of({
          message: 'Lo siento, ocurrió un error. Por favor intenta de nuevo.',
          type: 'text' as const
        });
      })
    );
  }

  // Get conversations list
  getConversations(page: number = 0, size: number = 20): Observable<PaginatedConversationsResult> {
    const headers = this.getHeaders();
    const userId = this.getUserId();

    const params: any = {
      page: page.toString(),
      size: size.toString(),
      tenantId: 'naaora'
    };

    if (userId) {
      params.userId = userId;
    }

    return this.http.get<PaginatedConversationsResponse>(
      `${environment.chatbotApiUrl}/v1/conversations`,
      { headers, params }
    ).pipe(
      map(response => {
        const conversations = response.conversations.map(dto =>
          this.mapConversationDtoToModel(dto)
        );

        return {
          conversations: conversations,
          page: response.page,
          size: response.size,
          totalElements: response.totalElements,
          totalPages: response.totalPages,
          last: response.last
        };
      }),
      catchError(error => {
        console.error('Error loading conversations:', error);
        return of({
          conversations: this.conversationsSubject.value,
          page: page,
          size: size,
          totalElements: this.conversationsSubject.value.length,
          totalPages: 1,
          last: true
        });
      })
    );
  }

  // Get current active conversation
  getCurrentConversation(): Observable<ChatConversation | null> {
    return this.currentConversation$;
  }

  // Set current conversation
  setCurrentConversation(conversation: ChatConversation | null): void {
    this.currentConversationSubject.next(conversation);
    if (conversation) {
      localStorage.setItem(this.CURRENT_CONVERSATION_KEY, conversation.id);
    } else {
      localStorage.removeItem(this.CURRENT_CONVERSATION_KEY);
    }
  }

  // Get conversation messages
  getConversationMessages(
    conversationId: string,
    page: number = 0,
    size: number = 50
  ): Observable<PaginatedMessagesResult> {
    const headers = this.getHeaders();

    return this.http.get<PaginatedMessagesResponse>(
      `${environment.chatbotApiUrl}/v1/conversations/${conversationId}/messages`,
      {
        headers,
        params: {
          page: page.toString(),
          size: size.toString()
        }
      }
    ).pipe(
      map(response => {
        const messages = response.messages.map(dto =>
          this.mapMessageDtoToModel(dto)
        );

        const sortedMessages = messages.sort((a, b) =>
          a.timestamp.getTime() - b.timestamp.getTime()
        );

        return {
          messages: sortedMessages,
          page: response.page,
          size: response.size,
          totalElements: response.totalElements,
          totalPages: response.totalPages,
          last: response.last
        };
      }),
      catchError(error => {
        console.error('Error loading conversation messages:', error);
        return of({
          messages: [],
          page: page,
          size: size,
          totalElements: 0,
          totalPages: 0,
          last: true
        });
      })
    );
  }

  // Create new conversation
  createConversation(firstMessage: string): ChatConversation {
    const tempConversation: ChatConversation = {
      id: 'temp_' + this.generateId(),
      title: this.generateConversationTitle(firstMessage),
      lastMessage: firstMessage,
      timestamp: new Date(),
      messages: [],
      unreadCount: 0
    };

    const conversations = this.conversationsSubject.value;
    conversations.unshift(tempConversation);
    this.conversationsSubject.next(conversations);

    // NOTE: Real conversation will be created by API when sendMessage is called
    // The conversationId returned from API will replace the temp ID

    return tempConversation;
  }

  // Update conversation ID after API creation
  updateConversationId(tempId: string, realId: string): void {
    const conversations = this.conversationsSubject.value;
    const conversation = conversations.find(c => c.id === tempId);

    if (conversation) {
      conversation.id = realId;
      this.conversationsSubject.next(conversations);

      // Update current conversation if it matches
      if (this.currentConversationSubject.value?.id === tempId) {
        this.currentConversationSubject.next(conversation);
      }
    }
  }

  // Add message to conversation
  addMessageToConversation(conversationId: string, message: ChatMessage): void {
    const conversations = this.conversationsSubject.value;
    const conversation = conversations.find(c => c.id === conversationId);

    if (conversation) {
      // Check if message already exists to avoid duplication
      const messageExists = conversation.messages.some(m => m.id === message.id);
      if (!messageExists) {
        conversation.messages.push(message);
      }

      conversation.lastMessage = message.text;
      conversation.timestamp = message.timestamp;

      this.conversationsSubject.next(conversations);
      // NOTE: No longer saving to localStorage - API is source of truth
    }
  }

  // ============================================
  // Help Center API Methods (HelpCenterAdminController)
  // ============================================

  /**
   * Get all help collections/containers with their articles
   * Calls: GET /admin/help-center/containers?locale=xx-XX
   */
  getHelpCollections(): Observable<HelpCollection[]> {
    const headers = this.getHeaders();
    const params = {
      locale: this.getLocale()
    };

    return this.http.get<HelpCollection[]>(
      `${this.helpCenterUrl}/containers`,
      { headers, params }
    ).pipe(
      catchError(error => {
        console.error('Error loading help collections:', error);
        return of([]);
      })
    );
  }

  /**
   * Search help articles by query
   * Calls: GET /admin/help-center/search?q=xxx&locale=xx-XX
   */
  searchHelpArticles(query: string): Observable<HelpArticle[]> {
    const headers = this.getHeaders();
    const params = {
      q: query,
      locale: this.getLocale()
    };

    return this.http.get<HelpArticle[]>(
      `${this.helpCenterUrl}/search`,
      { headers, params }
    ).pipe(
      catchError(error => {
        console.error('Error searching help articles:', error);
        return of([]);
      })
    );
  }

  /**
   * Get help article by ID (also increments view count)
   * Calls: GET /admin/help-center/articles/{id}?locale=xx-XX
   */
  getHelpArticleById(articleId: string): Observable<HelpArticle | null> {
    const headers = this.getHeaders();
    const params = {
      locale: this.getLocale()
    };

    return this.http.get<HelpArticle>(
      `${this.helpCenterUrl}/articles/${articleId}`,
      { headers, params }
    ).pipe(
      catchError(error => {
        console.error('Error loading help article:', error);
        return of(null);
      })
    );
  }

  /**
   * Get popular help articles
   * Calls: GET /admin/help-center/articles/popular?limit=N&locale=xx-XX
   */
  getPopularArticles(limit: number = 5): Observable<HelpArticle[]> {
    const headers = this.getHeaders();
    const params = {
      limit: limit.toString(),
      locale: this.getLocale()
    };

    return this.http.get<HelpArticle[]>(
      `${this.helpCenterUrl}/articles/popular`,
      { headers, params }
    ).pipe(
      catchError(error => {
        console.error('Error loading popular articles:', error);
        return of([]);
      })
    );
  }

  // Deprecated: Use getHelpArticleById instead
  getHelpArticle(articleId: string): Observable<HelpArticle | null> {
    return this.getHelpArticleById(articleId);
  }

  /**
   * Fetch content from a URL using HttpClient
   * This avoids CORS issues by using Angular's HttpClient with proper headers
   */
  fetchContentFromUrl(url: string): Observable<string> {
    const headers = this.getHeaders();

    return this.http.get(url, {
      headers,
      responseType: 'text'
    }).pipe(
      catchError(error => {
        console.error('Error fetching content from URL:', error);
        return of('');
      })
    );
  }

  // Clear all conversations
  clearConversations(): void {
    this.conversationsSubject.next([]);
    this.currentConversationSubject.next(null);
    localStorage.removeItem(this.STORAGE_KEY);
    localStorage.removeItem(this.CURRENT_CONVERSATION_KEY);
  }

  // Delete specific conversation
  deleteConversation(conversationId: string): void {
    const conversations = this.conversationsSubject.value.filter(c => c.id !== conversationId);
    this.conversationsSubject.next(conversations);
    this.saveConversationsToStorage();

    if (this.currentConversationSubject.value?.id === conversationId) {
      this.currentConversationSubject.next(null);
    }
  }

  // Private helper methods
  private getHeaders(): HttpHeaders {
    const token = localStorage.getItem(GenericConstants.STORAGE_ACCESS_TOKEN);
    return new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: token || ''
    });
  }

  private generateId(): string {
    return `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private generateConversationTitle(message: string): string {
    const maxLength = 50;
    return message.length > maxLength
      ? message.substring(0, maxLength) + '...'
      : message;
  }

  // Deprecated: Conversations now loaded from API
  private loadConversationsFromStorage(): void {
    console.warn('localStorage conversation loading is deprecated - using API instead');
  }

  // Deprecated: Conversations now persisted via API
  private saveConversationsToStorage(): void {
    console.warn('localStorage conversation saving is deprecated - using API instead');
  }

  // NOTE: Mock methods removed - now using real Help Center API
}
